/* AgentMeeting — Docs page (single-page docs portal) */

import React from 'react';
import { useI18n } from '../shared/i18n';
import { MarketingBar, MarketingFooter } from '../shared/marketing-bar';
import { I } from '../shared/ui';

export function Docs({ goTo, anchor }) {
  const { t, lang } = useI18n();
  const [active, setActive] = React.useState(anchor || 'intro');

  React.useEffect(() => {
    if (anchor) {
      setActive(anchor);
      const el = document.getElementById('doc-' + anchor);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [anchor]);

  const sections = lang === 'zh' ? DOC_SECTIONS_ZH : DOC_SECTIONS_EN;

  return (
    <div className="mkt-page">
      <MarketingBar here="docs" goTo={goTo} signedIn={false} />

      <div style={{ maxWidth: 1240, margin: '0 auto', padding: '0 28px',
        display: 'grid', gridTemplateColumns: '220px 1fr 200px', gap: 48,
        alignItems: 'start',
      }} className="docs-grid">
        {/* sidebar */}
        <aside style={{
          position: 'sticky', top: 80,
          fontFamily: 'var(--mono)', fontSize: 12,
          paddingTop: 56,
          maxHeight: 'calc(100vh - 80px)', overflowY: 'auto',
        }}>
          <div className="mono tiny dim" style={{ letterSpacing: '0.16em', marginBottom: 14 }}>
            DOCS · v0.4.2
          </div>
          {sections.map(grp => (
            <div key={grp.id} style={{ marginBottom: 20 }}>
              <div className="mono" style={{
                fontSize: 10, color: 'var(--fg-3)', letterSpacing: '0.12em',
                textTransform: 'uppercase', marginBottom: 8,
              }}>
                {grp.label}
              </div>
              <div className="col" style={{ gap: 4 }}>
                {grp.items.map(it => (
                  <a key={it.id}
                    onClick={() => {
                      setActive(it.id);
                      const el = document.getElementById('doc-' + it.id);
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    style={{
                      cursor: 'pointer', padding: '4px 10px',
                      borderLeft: '2px solid ' + (active === it.id ? 'var(--live)' : 'transparent'),
                      color: active === it.id ? 'var(--fg-0)' : 'var(--fg-2)',
                      background: active === it.id ? 'var(--bg-1)' : 'transparent',
                      fontSize: 12,
                    }}>
                    {it.title}
                  </a>
                ))}
              </div>
            </div>
          ))}
        </aside>

        {/* main */}
        <main style={{ paddingTop: 56, paddingBottom: 120, minWidth: 0 }}>
          <div className="mono tiny" style={{ color: 'var(--live)', letterSpacing: '0.16em' }}>
            [DOCUMENTATION]
          </div>
          <h1 style={{
            fontSize: 'clamp(32px, 4vw, 48px)',
            margin: '8px 0 8px', letterSpacing: '-0.02em', fontWeight: 500,
          }}>
            {lang === 'zh' ? '让 6 个 AI 替你开会' : 'Let 6 AIs run your meeting'}
          </h1>
          <p style={{ color: 'var(--fg-1)', fontSize: 16, lineHeight: 1.6, maxWidth: 720 }}>
            {lang === 'zh'
              ? '从 5 分钟 quickstart, 到调度策略源码, 全在这里。看完你能自己起一场会。'
              : 'From a 5-minute quickstart to scheduling-strategy internals — everything to run your first meeting.'}
          </p>

          <div className="flex gap-3 mono tiny" style={{ marginTop: 18, flexWrap: 'wrap' }}>
            {[
              [lang === 'zh' ? '5 分钟入门' : '5-min quickstart', 'quickstart'],
              [lang === 'zh' ? 'API 参考' : 'API reference', 'api'],
              [lang === 'zh' ? '调度策略' : 'Scheduling', 'scheduling'],
              [lang === 'zh' ? 'Cookbook' : 'Cookbook', 'cookbook'],
            ].map(([label, id]) => (
              <a key={id} onClick={() => setActive(id)}
                className="tag mono"
                style={{ borderColor: 'var(--line-3)', cursor: 'pointer' }}>
                → {label}
              </a>
            ))}
          </div>

          {/* doc body */}
          <div style={{ marginTop: 56 }}>
            <DocSection id="intro"
              kicker={lang === 'zh' ? '介绍' : 'INTRO'}
              title={lang === 'zh' ? '什么是 AgentMeeting' : 'What is AgentMeeting'}>
              <p>{lang === 'zh'
                ? 'AgentMeeting 把多个 LLM agent 放在同一张圆桌上, 让它们围绕一个议题进行结构化的辩论 / 决策 / 红蓝对抗。它不是把几个 chatbot 拼起来的 wrapper, 而是一个有「议程、轮次、调度策略、投票、纪要」语义的会议平台。'
                : 'AgentMeeting puts multiple LLM agents at one round table and has them debate / decide / red-team a topic in a structured way. It is not a wrapper around a few chatbots — it is a meeting platform with first-class semantics for agendas, rounds, scheduling, voting and minutes.'}
              </p>
              <ul className="doc-ul">
                <li><b>{lang === 'zh' ? '多模型混合' : 'Multi-model'}</b> · Claude · GPT · Gemini · Llama · Qwen</li>
                <li><b>{lang === 'zh' ? '调度即规则' : 'Scheduling-as-rules'}</b> · round-robin · stance-pair · reactive · moderated · free-form</li>
                <li><b>{lang === 'zh' ? '人在回路' : 'Human-in-the-loop'}</b> · {lang === 'zh' ? '主持人 / 参会者 / 导演视角' : 'Host / participant / director'}</li>
                <li><b>{lang === 'zh' ? '可观测' : 'Observable'}</b> · {lang === 'zh' ? '回放 / 分叉 / 单条 prompt 透明化' : 'replay / fork / per-message prompt inspector'}</li>
              </ul>
            </DocSection>

            <DocSection id="quickstart"
              kicker={lang === 'zh' ? '快速开始 · 5 分钟' : 'QUICKSTART · 5 MIN'}
              title={lang === 'zh' ? '开你的第一场会' : 'Run your first meeting'}>
              <DocStep n="1"
                title={lang === 'zh' ? '从模板派生' : 'Fork a starter pack'}
                body={lang === 'zh'
                  ? '在 Studio · Starter packs 里选一个最接近的: 决策会议 / 红蓝对抗 / 苏格拉底 / 头脑风暴 — 它会一次性帮你把 4 ~ 6 个 agent 都建好。'
                  : 'In Studio · Starter packs, pick the closest match — Decision meeting / Red-blue / Socratic / Brainstorm. It scaffolds 4–6 agents for you.'} />
              <DocStep n="2"
                title={lang === 'zh' ? '设议程' : 'Set the agenda'}
                body={lang === 'zh'
                  ? '在 Agenda editor 里决定开几轮、每轮什么调度策略、终止条件(预算 / 阈值 / 无新论点)。'
                  : 'In Agenda editor, decide round count, each round\'s scheduling strategy, and stop conditions (budget / threshold / no-new-arguments).'} />
              <DocStep n="3"
                title={lang === 'zh' ? '开会' : 'Run it'}
                body={lang === 'zh'
                  ? '回车开始。你可以旁观、参与、暂停、改 prompt、fork 重跑。'
                  : 'Hit run. You can observe, participate, pause, edit prompts, or fork a branch.'} />
              <DocStep n="4"
                title={lang === 'zh' ? '收口' : 'Resolve'}
                body={lang === 'zh'
                  ? '到投票轮自动唱票 → 评委判词 → 系统生成纪要(决议 / 反对 / 行动项 / 引用源)。'
                  : 'Voting rounds tally automatically → judges hand down a verdict → minutes are generated with decision / dissent / action items / citations.'} />
            </DocSection>

            <DocSection id="concepts"
              kicker={lang === 'zh' ? '核心概念' : 'CORE CONCEPTS'}
              title={lang === 'zh' ? '议程 · 轮次 · 调度' : 'Agenda · Rounds · Scheduling'}>
              <p>{lang === 'zh'
                ? '一场会议 = 一份议程, 议程由若干轮组成, 每一轮挂一个调度策略 + 一段 instruction + 一个终止条件。轮内每个 agent 收到的 prompt 都包含: system / 知识库片段 / rolling summary / transcript / 当轮指令。'
                : 'A meeting = an agenda. An agenda is a list of rounds. Each round carries a scheduling strategy + an instruction + a stop condition. Inside a round, every agent\'s prompt contains: system / KB snippets / rolling summary / transcript / round-specific instruction.'}
              </p>
              <DocCallout>
                {lang === 'zh' ? '⌗ 想看一个 agent 实际收到了什么? Room 里点任一发言右上角的 ⌗ context, 整段 prompt 会展开。'
                  : '⌗ Want to see what an agent actually got? In Room, click ⌗ context on any message — the full prompt unfurls.'}
              </DocCallout>
            </DocSection>

            <DocSection id="scheduling"
              kicker={lang === 'zh' ? '调度' : 'SCHEDULING'}
              title={lang === 'zh' ? '6 种内置调度策略' : '6 built-in strategies'}>
              <DocTable
                headers={[lang === 'zh' ? '策略' : 'strategy', lang === 'zh' ? '行为' : 'behavior', lang === 'zh' ? '适合' : 'good for']}
                rows={lang === 'zh' ? [
                  ['round-robin', '按 agent 列表轮发, 每人 1 次', '开场陈述、闭幕总结'],
                  ['stance-pair', '红 / 蓝阵营轮流交锋', '辩论、对抗审计'],
                  ['reactive', '基于关注度 / @ / 矛盾度选下一发言人', '修正、回应'],
                  ['moderated', '主持人(可以是 LLM)逐个点名', '研讨会、答辩'],
                  ['vote', '只允许投票 + 一句理由', '决议轮'],
                  ['free-form', '随机 / 模型自选', '头脑风暴'],
                ] : [
                  ['round-robin', 'each agent speaks once in order', 'opening statements, closing'],
                  ['stance-pair', 'red / blue camps alternate', 'debate, adversarial audit'],
                  ['reactive', 'next speaker by attention / @ / disagreement', 'rebuttals, follow-ups'],
                  ['moderated', 'a moderator (often LLM) calls on each', 'panels, defenses'],
                  ['vote', 'each agent votes + 1-line rationale', 'resolution rounds'],
                  ['free-form', 'random / model picks', 'brainstorm'],
                ]} />
            </DocSection>

            <DocSection id="api"
              kicker="API"
              title={lang === 'zh' ? 'REST · 启动一场会议' : 'REST · start a meeting'}>
              <p>{lang === 'zh'
                ? 'Base URL ·' : 'Base URL ·'} <code className="mono" style={{ color: 'var(--live)' }}>https://api.agentmeeting.dev/v1</code>
              </p>
              <CodeBlock lang="bash" code={`# auth: bearer token from settings → API keys
curl https://api.agentmeeting.dev/v1/meetings \\
  -H "Authorization: Bearer am_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "agenda_id": "agenda_q3_brazil",
    "agents":   ["agent_ceo", "agent_cfo", "agent_cro", "agent_legal", "agent_user"],
    "budget_usd": 5.00,
    "stream":   true
  }'`} />

              <p style={{ marginTop: 24 }}>
                {lang === 'zh' ? '响应是 SSE 流, 每条事件:' : 'Response is an SSE stream. Each event:'}
              </p>
              <CodeBlock lang="json" code={`event: turn.delta
data: {"meeting_id":"mtg_4f7c","turn_id":"t_19","agent_id":"agent_cfo","delta":"@Aria CAC 翻 1.8x","tokens_in":1287,"tokens_out":92,"cost_usd":0.0041}`} />
            </DocSection>

            <DocSection id="sdk"
              kicker="SDK"
              title={lang === 'zh' ? 'Python / TypeScript' : 'Python / TypeScript'}>
              <CodeBlock lang="python" code={`from agentmeeting import Meeting, Agent, RoundRobin, StancePair

m = Meeting(topic="Q3 进巴西?", budget_usd=5.0)
m.add(Agent("CEO Aria",  model="claude-sonnet-4.5", stance="pro",     persona="..."))
m.add(Agent("CFO Bran",  model="gpt-5",             stance="against", persona="..."))
m.add(Agent("Legal",     model="gemini-2.5-pro",    stance="neutral", persona="..."))

m.round("opening",    RoundRobin())
m.round("rebuttal",   StancePair(rounds=2))
m.round("vote",       Vote(threshold=0.66))

for turn in m.run():       # streams
    print(f"{turn.agent}: {turn.text}")
print(m.minutes())`} />
            </DocSection>

            <DocSection id="cookbook"
              kicker="COOKBOOK"
              title={lang === 'zh' ? '现成的会议' : 'Ready-made meetings'}>
              <div className="grid" style={{ gap: 12, gridTemplateColumns: 'repeat(2, 1fr)' }}>
                {[
                  ['red-blue.py', lang === 'zh' ? '红蓝对抗审计' : 'Red-blue audit'],
                  ['decision.py', lang === 'zh' ? '高管决策会' : 'Exec decision'],
                  ['socratic.py', lang === 'zh' ? '苏格拉底教学' : 'Socratic tutor'],
                  ['triage.py',   lang === 'zh' ? '工单分诊' : 'Ticket triage'],
                  ['rfc.py',      lang === 'zh' ? '工程 RFC 评审' : 'Engineering RFC'],
                  ['retro.py',    lang === 'zh' ? '迭代复盘' : 'Sprint retro'],
                ].map(([f, label]) => (
                  <a key={f} className="hov" style={{
                    padding: 14, border: '1px solid var(--line-2)', borderRadius: 6,
                    background: 'var(--bg-1)', cursor: 'pointer',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <div>
                      <div className="mono" style={{ fontSize: 12, color: 'var(--live)' }}>{f}</div>
                      <div style={{ fontSize: 13, marginTop: 4 }}>{label}</div>
                    </div>
                    <span className="dim mono">→</span>
                  </a>
                ))}
              </div>
            </DocSection>

            <DocSection id="byok"
              kicker="BYOK"
              title={lang === 'zh' ? '用自己的 API key' : 'Bring your own keys'}>
              <p>{lang === 'zh'
                ? '在 Settings · LLM & API keys 里贴你的 Anthropic / OpenAI / Google key, 平台对 token 0% markup, 只收会议时长费($0.04/min)。'
                : 'Drop your Anthropic / OpenAI / Google keys in Settings · LLM & API keys. We charge 0% on tokens — only meeting time ($0.04/min).'}
              </p>
              <DocCallout warn>
                {lang === 'zh'
                  ? '⚠ key 只在你的浏览器解密 — 服务端只看到密文。'
                  : '⚠ keys are decrypted only in your browser — the server sees ciphertext.'}
              </DocCallout>
            </DocSection>

            <DocSection id="faq"
              kicker="FAQ"
              title={lang === 'zh' ? '常见问题' : 'Frequently asked'}>
              {(lang === 'zh' ? FAQ_ZH : FAQ_EN).map((qa, i) => (
                <details key={i} style={{
                  borderTop: '1px solid var(--line-1)', padding: '14px 0',
                }}>
                  <summary style={{ cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
                    {qa.q}
                  </summary>
                  <p style={{ marginTop: 8, color: 'var(--fg-1)', lineHeight: 1.6, fontSize: 13 }}>
                    {qa.a}
                  </p>
                </details>
              ))}
            </DocSection>

            <div style={{
              marginTop: 80, padding: 24, border: '1px dashed var(--line-2)',
              borderRadius: 6, background: 'var(--bg-1)',
            }}>
              <div className="flex between center wrap" style={{ gap: 16 }}>
                <div>
                  <div className="mono tiny" style={{ color: 'var(--live)', letterSpacing: '0.12em' }}>
                    [READY?]
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 500, marginTop: 4 }}>
                    {lang === 'zh' ? '5 分钟开你的第一场会。' : 'Run your first meeting in 5 min.'}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button className="btn primary mono" onClick={() => goTo('signup')}>
                    {t('cta.startFree')}
                  </button>
                  <button className="btn ghost mono" onClick={() => goTo('app/agenda')}>
                    {t('cta.openApp')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* on-this-page */}
        <aside style={{
          position: 'sticky', top: 80, paddingTop: 56,
          fontFamily: 'var(--mono)', fontSize: 11,
        }} className="docs-otp">
          <div className="dim" style={{ letterSpacing: '0.12em', marginBottom: 10 }}>
            ON THIS PAGE
          </div>
          <div className="col gap-2">
            {['intro', 'quickstart', 'concepts', 'scheduling', 'api', 'sdk', 'cookbook', 'byok', 'faq'].map(id => (
              <a key={id} onClick={() => {
                setActive(id);
                document.getElementById('doc-' + id)?.scrollIntoView({ behavior: 'smooth' });
              }} style={{
                color: active === id ? 'var(--live)' : 'var(--fg-2)',
                cursor: 'pointer',
              }}>
                {id === 'api' || id === 'sdk' || id === 'faq' ? id.toUpperCase() : id}
              </a>
            ))}
          </div>
        </aside>
      </div>

      <MarketingFooter goTo={goTo} />
    </div>
  );
}

function DocSection({ id, kicker, title, children }) {
  return (
    <section id={'doc-' + id} style={{
      paddingTop: 24, paddingBottom: 40,
      borderTop: '1px solid var(--line-1)', marginTop: 24,
    }}>
      <div className="mono tiny" style={{ color: 'var(--live)', letterSpacing: '0.14em' }}>
        [{kicker}]
      </div>
      <h2 style={{
        margin: '8px 0 24px', fontSize: 28,
        letterSpacing: '-0.02em', fontWeight: 500,
      }}>
        {title}
      </h2>
      <div className="doc-prose" style={{ color: 'var(--fg-1)', lineHeight: 1.7, fontSize: 14 }}>
        {children}
      </div>
    </section>
  );
}

function DocStep({ n, title, body }) {
  return (
    <div className="flex" style={{
      gap: 16, padding: '14px 0', borderBottom: '1px solid var(--line-1)',
    }}>
      <div className="mono" style={{
        width: 28, height: 28, borderRadius: 4,
        background: 'var(--bg-2)', color: 'var(--live)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 12, flexShrink: 0,
      }}>{n}</div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--fg-0)' }}>{title}</div>
        <div style={{ marginTop: 4, fontSize: 13 }}>{body}</div>
      </div>
    </div>
  );
}

function DocCallout({ children, warn }) {
  return (
    <div style={{
      margin: '16px 0', padding: '12px 14px',
      borderLeft: '3px solid ' + (warn ? 'var(--err)' : 'var(--live)'),
      background: 'var(--bg-1)', borderRadius: '0 4px 4px 0',
      fontSize: 13, color: 'var(--fg-1)',
    }}>
      {children}
    </div>
  );
}

function DocTable({ headers, rows }) {
  return (
    <div style={{
      border: '1px solid var(--line-2)', borderRadius: 6, overflow: 'hidden',
      marginTop: 12, background: 'var(--bg-1)',
    }}>
      <div className="grid mono tiny" style={{
        gridTemplateColumns: '160px 1fr 1.4fr',
        background: 'var(--bg-2)', padding: '10px 14px',
        color: 'var(--fg-3)', letterSpacing: '0.08em',
        textTransform: 'uppercase',
      }}>
        {headers.map(h => <div key={h}>{h}</div>)}
      </div>
      {rows.map((r, i) => (
        <div key={i} className="grid" style={{
          gridTemplateColumns: '160px 1fr 1.4fr', gap: 12,
          padding: '12px 14px', fontSize: 13,
          borderBottom: i < rows.length - 1 ? '1px solid var(--line-1)' : 'none',
          alignItems: 'center',
        }}>
          <div className="mono" style={{ color: 'var(--live)', fontSize: 12 }}>{r[0]}</div>
          <div style={{ color: 'var(--fg-1)' }}>{r[1]}</div>
          <div className="dim" style={{ fontSize: 12 }}>{r[2]}</div>
        </div>
      ))}
    </div>
  );
}

function CodeBlock({ lang, code }) {
  return (
    <div style={{
      marginTop: 14, border: '1px solid var(--line-2)', borderRadius: 6,
      background: '#08090b', overflow: 'hidden',
    }}>
      <div style={{
        padding: '8px 14px', borderBottom: '1px solid var(--line-1)',
        fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--fg-3)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span>{lang}</span>
        <span style={{ cursor: 'pointer' }} className="link">copy</span>
      </div>
      <pre style={{
        margin: 0, padding: '14px 16px', fontFamily: 'var(--mono)', fontSize: 12,
        lineHeight: 1.65, color: 'var(--fg-1)', overflowX: 'auto',
      }}>{code}</pre>
    </div>
  );
}

const DOC_SECTIONS_ZH = [
  { id: 'g1', label: '入门', items: [
    { id: 'intro',      title: '什么是 AgentMeeting' },
    { id: 'quickstart', title: '5 分钟入门' },
    { id: 'concepts',   title: '核心概念' },
  ]},
  { id: 'g2', label: '使用', items: [
    { id: 'scheduling', title: '调度策略' },
    { id: 'cookbook',   title: '现成的会议' },
    { id: 'byok',       title: 'BYOK · 自带 key' },
  ]},
  { id: 'g3', label: '开发', items: [
    { id: 'api',        title: 'REST API' },
    { id: 'sdk',        title: 'Python / TS SDK' },
  ]},
  { id: 'g4', label: '其他', items: [
    { id: 'faq',        title: '常见问题' },
  ]},
];

const DOC_SECTIONS_EN = [
  { id: 'g1', label: 'Get started', items: [
    { id: 'intro',      title: 'What is AgentMeeting' },
    { id: 'quickstart', title: 'Quickstart · 5 min' },
    { id: 'concepts',   title: 'Core concepts' },
  ]},
  { id: 'g2', label: 'Use', items: [
    { id: 'scheduling', title: 'Scheduling' },
    { id: 'cookbook',   title: 'Cookbook' },
    { id: 'byok',       title: 'BYOK · own keys' },
  ]},
  { id: 'g3', label: 'Build', items: [
    { id: 'api',        title: 'REST API' },
    { id: 'sdk',        title: 'Python / TS SDK' },
  ]},
  { id: 'g4', label: 'Other', items: [
    { id: 'faq',        title: 'FAQ' },
  ]},
];

const FAQ_ZH = [
  { q: '我能用自己的 OpenAI / Anthropic / Gemini key 吗?',
    a: '可以。Settings · LLM 里粘贴 key, 平台对 token 收 0% markup, 只收 $0.04/min 的会议时长费。' },
  { q: 'agent 之间会重复说话吗? 怎么避免无限循环?',
    a: '调度策略本身决定每轮谁说话, 加上停止条件(预算 / 论点重复度 / 投票阈值), 会议必然终止。' },
  { q: '我能在中途加入会议或修改某个 agent 的 prompt 吗?',
    a: '能。导演视角下你可以注入 system note、改 persona、踢 agent、邀请新 agent 进来。Replay 还能回到任意时点 fork 一条平行线。' },
  { q: '数据安全怎么保证?',
    a: 'Soc 2 Type II in progress · 数据 EU/US 双区域 · API key 浏览器端加密 · 你可以一键导出 / 删除全部会议。' },
  { q: '免费版有什么限制?',
    a: 'Free 版每月 60 min 时长 + $1 LLM credit, 同时最多 4 个 agent · 无 API 访问。够你打一两场决策会。' },
];

const FAQ_EN = [
  { q: 'Can I use my own OpenAI / Anthropic / Gemini key?',
    a: 'Yes. Drop them in Settings · LLM. We charge 0% on tokens — only $0.04/min meeting time.' },
  { q: 'Will agents loop forever?',
    a: 'No. Scheduling decides who speaks each round; stop conditions (budget / argument-repetition / vote threshold) terminate the meeting.' },
  { q: 'Can I jump in mid-meeting or rewrite an agent\'s prompt?',
    a: 'Yes. As director, inject a system note, edit persona, kick an agent, or invite a new one. Replay also lets you fork from any point.' },
  { q: 'How is my data secured?',
    a: 'SOC 2 Type II in progress · EU/US dual-region · API keys encrypted browser-side · one-click export and delete all meetings.' },
  { q: 'What are the free-tier limits?',
    a: 'Free: 60 min/month, $1 LLM credit, up to 4 agents at once · no API access. Enough for 1–2 decision meetings.' },
];
